import { html, LitElement, nothing, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_EASM_GENERIC_TOP_DATA } from '../../../../webcomponents.constants';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { FilterType, RowData, SEVERITY, SeverityData } from '../types/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { generateFiltersForInventory } from '../../../utils/utils';
import { FindingClassType } from '../../../types';

@customElement(WEB_COMPONENT_EASM_GENERIC_TOP_DATA)
export class GzEasmGenericTopDataWebComponent extends LitElement {
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  rowsData: Array<RowData>;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  showSeverity: Boolean = false;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  showDate: Boolean = false;

  @property({
    type: Number,
    attribute: true,
    reflect: true,
  })
  numberOfRowsToDisplay: Number;

  @property({
    type: Number,
    attribute: true,
    reflect: true,
  })
  minNumberOfRowsToDisplay: Number;

  @property({
    type: Number,
    attribute: true,
    reflect: true,
  })
  numberOfRowsAvailable: Number;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  buttonText: String;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  navigateToSmartViewId: String;

  @property({
    type: FilterType,
    attribute: true,
    reflect: true,
  })
  filterType: FilterType;

  @property({
    type: FindingClassType,
    attribute: true,
    reflect: true,
  })
  findingType: FindingClassType;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  clickableRow = true;

  static get styles() {
    return styles;
  }

  protected render(): TemplateResult {
    const items = [] as TemplateResult[];
    let rowsToDisplay;
    if (this.minNumberOfRowsToDisplay >= this.numberOfRowsAvailable) {
      rowsToDisplay = this.minNumberOfRowsToDisplay;
    } else if (this.numberOfRowsToDisplay >= this.numberOfRowsAvailable) {
      rowsToDisplay = this.numberOfRowsAvailable;
    } else {
      rowsToDisplay = this.numberOfRowsToDisplay;
    }

    for (let indexOfItem = 0; indexOfItem < rowsToDisplay; indexOfItem++) {
      items.push(this.renderRow(this.rowsData[indexOfItem]));
    }

    return html`
      <div class="section">
        <div class="items">${items}</div>
        ${this.renderButtonIfTheTextExist()}
      </div>
    `;
  }

  public rerender() {
    this.render();
  }

  public renderButtonIfTheTextExist(): TemplateResult | typeof nothing {
    if (this.buttonText) {
      return html`
        <gz-link id="view-all-link" @click="${async () => await this.navigateToInventory()}">
          <gz-text fontSize="${FontSizes.PARAGRAPH_11}" fontWeight="${FontWeights.MEDIUM}">
            ${this.buttonText}
          </gz-text>
        </gz-link>
      `;
    } else {
      return nothing;
    }
  }

  protected renderRow(
    rowData: RowData = {
      label: null,
      date: null,
      value: null,
    }
  ): TemplateResult {
    let rowValue: TemplateResult;
    const label = rowData.label ?? '...';
    const value = rowData.value ?? '--';
    if (this.showSeverity) {
      const severityData = value as SeverityData;
      const displayValue = severityData.severity
        ? this.translateSeverity(severityData.severity) + ' (' + severityData.percentage + '%)'
        : '...';

      rowValue = html`
        <div class="item-score">
          <div class="item-severity-score">${displayValue}</div>
          <div class="item-bullet">${this.renderBullet(severityData.severity)}</div>
        </div>
      `;
    } else {
      rowValue = html`
        <gz-tooltip class="value-tooltip" isEllipsis tooltipText="${value}">
          <div class="item-value">${value}</div>
        </gz-tooltip>
      `;
    }

    let date;

    if (this.showDate) {
      rowData.date = rowData.date ?? '--';
      date = html`
        <gz-tooltip class="date-tooltip" isEllipsis tooltipText="${rowData.date}">
          <div class="item-date">${rowData.date}</div>
        </gz-tooltip>
      `;
    } else {
      date = nothing;
    }

    if (!this.clickableRow || !this.navigateToSmartViewId || !rowData.value) {
      return html`
        <div class="row"">
          <div class="item-info">
            <gz-tooltip class="label-tooltip" isEllipsis tooltipText="${label}">
              <div class="item-name">${label}</div>
            </gz-tooltip>
            ${date} ${rowValue}
          </div>
        </div>
      `;
    }

    return html`
      <div class="row clickable" @click="${async () => await this.navigateToInventory(label)}">
        <div class="item-info">
          <gz-tooltip class="label-tooltip" isEllipsis tooltipText="${label}">
            <div class="item-name">${label}</div>
          </gz-tooltip>
          ${date} ${rowValue}
        </div>
      </div>
    `;
  }

  private translateSeverity(severity: number): string {
    switch (severity) {
      case SEVERITY.HIGH:
        return translateFn('High');
      case SEVERITY.MEDIUM:
        return translateFn('Medium');
      case SEVERITY.LOW:
        return translateFn('Low');
      default:
        return translateFn('Unknown');
    }
  }

  private renderBullet(severity: number): TemplateResult | symbol {
    return html`<span class="cell-label has-bullet ${this.getBulletClassFromType(severity)}"></span>`;
  }

  private getBulletClassFromType(severity: number): string {
    switch (severity) {
      case SEVERITY.HIGH:
        return 'bullet-red';
      case SEVERITY.MEDIUM:
        return 'bullet-orange';
      case SEVERITY.LOW:
        return 'bullet-yellow';
      default:
        return 'bullet-neutral';
    }
  }

  //TODO - change to smartViewLabel
  async navigateToInventory(filterData: string | null = null): Promise<void> {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const filtersToSend = generateFiltersForInventory(this.filterType, filterData, companyId);

    if (this.findingType === FindingClassType.ASSET) {
      await NavigationHelper.navigateToEASMAssetsInventory({
        smartViewId: this.navigateToSmartViewId as string,
        filters: filtersToSend,
      });
    } else if (this.findingType === FindingClassType.ARTIFACT) {
      await NavigationHelper.navigateToEASMArtifactsInventory({
        smartViewId: this.navigateToSmartViewId as string,
        filters: filtersToSend,
      });
    }
  }
}
